import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'

import { Person } from '../v3/figures'

/**
 * Bühne der Exkurse: eine feste Leinwand (640 × 420), die sich in ihren
 * Container skaliert. Elemente bekommen pro Schritt eine Pose, CSS
 * übernimmt den Übergang. So bleiben die Szenen deklarativ.
 */
export const CANVAS = { w: 640, h: 420 }

export function ExStage({ step, className, children }) {
  const box = useRef(null)
  const canvas = useRef(null)

  useEffect(() => {
    const el = box.current
    if (!el) return undefined
    const fit = () => {
      const s = Math.min(el.clientWidth / CANVAS.w, el.clientHeight / CANVAS.h)
      canvas.current.style.transform = `translate(-50%, -50%) scale(${s})`
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <div ref={box} className={clsx('ix-stage', className)} data-step={step}>
      <div
        ref={canvas}
        className='ix-canvas'
        style={{ width: CANVAS.w, height: CANVAS.h }}
      >
        {children}
      </div>
    </div>
  )
}

ExStage.propTypes = {
  step: PropTypes.number.isRequired,
  className: PropTypes.string,
  children: PropTypes.node
}

/** Letzte Pose, deren Schritt kleiner oder gleich `step` ist. */
export const poseAt = (step, poses) => {
  const keys = Object.keys(poses)
    .map(Number)
    .filter((k) => k <= step)
  return keys.length ? poses[Math.max(...keys)] : { o: 0 }
}

/**
 * Absolut platziertes Element. `poses` = { schritt: { x, y, s, r, o } },
 * fehlende Werte fallen auf 0 (Position), 1 (Skalierung, Deckkraft) zurück.
 */
export function Pose({ step, poses, delay = 0, className, style, children }) {
  const p = poseAt(step, poses)
  return (
    <div
      className={clsx('ix-pose', className)}
      style={{
        transform: `translate(${p.x || 0}px, ${p.y || 0}px) rotate(${p.r || 0}deg) scale(${p.s ?? 1})`,
        opacity: p.o ?? 1,
        transitionDelay: `${delay}s`,
        ...style
      }}
    >
      {children}
    </div>
  )
}

Pose.propTypes = {
  step: PropTypes.number.isRequired,
  poses: PropTypes.object.isRequired,
  delay: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node
}

// Ein Bereich von --l, der nie bzw. immer erreicht ist: neutrales oder
// lächelndes Gesicht, unabhängig vom Scrollstand der Geschichte
const NEVER = [2, 3]
const ALWAYS = [-2, -1]

/** Figur aus dem Office als HTML-Block, Fußpunkt unten mittig. */
export function Fig({ body, skin, hair, happy, size = 1, walk }) {
  return (
    <svg
      className={clsx('ix-fig', walk && 'ix-fig--walk')}
      viewBox='-22 -70 44 76'
      width={44 * size}
      height={76 * size}
      aria-hidden='true'
    >
      <Person
        body={body}
        skin={skin}
        hair={hair}
        happy={happy ? ALWAYS : NEVER}
      />
    </svg>
  )
}

Fig.propTypes = {
  body: PropTypes.string.isRequired,
  skin: PropTypes.string.isRequired,
  hair: PropTypes.string,
  happy: PropTypes.bool,
  size: PropTypes.number,
  walk: PropTypes.bool
}

// Ein paar Looks für Talente, passend zu den Figuren im Office
export const LOOKS = [
  { body: 'pink', skin: '#F1D3B3', hair: '#2B1D14' },
  { body: 'yellow', skin: '#C68B59', hair: '#2B1D14' },
  { body: 'green', skin: '#8D5524', hair: '#182033' },
  { body: '#4461D3', skin: '#F2C9A5', hair: '#7A4A2A' },
  { body: '#C9A7F2', skin: '#F1D3B3', hair: '#E6C27A' },
  { body: '#7FD3E6', skin: '#8D5524', hair: '#182033' },
  { body: '#F2B48F', skin: '#C68B59', hair: '#2B1D14' },
  { body: '#9FE0C2', skin: '#E0AC69', hair: '#5A3825' }
]
